import { useEffect, useState } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { toast } from "react-toastify";

import { fetchAllTicketStatusesByDepartment, updateTicketStatus } from "../ticketStatuses/ticketStatusesApi";
import { TicketStatus } from "../ticketStatuses/TicketStatus";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import LoadingSpinner from "../../common/components/loadingSpinner/LoadingSpinner";
import { countByStatus } from "../tickets/ticketsApi";
import { confirmDialog } from "primereact/confirmdialog";

interface TicketStatusesTableProps {
  departmentId: string;
}

export default function TicketStatusesTable({ departmentId }: TicketStatusesTableProps) {
  const [ticketStatuses, setTicketStatuses] = useState<TicketStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    doFetchTicketStatuses();
  }, [departmentId]);

  const doFetchTicketStatuses = async () => {
    if (departmentId) {
      setIsLoading(true);
      const ticketStatuses = await fetchAllTicketStatusesByDepartment(departmentId);
      setTicketStatuses(ticketStatuses);
      setIsLoading(false);
    }
  };

  const doUpdateTicketStatus = async (index: number, ticketStatus: TicketStatus) => {
    setIsLoading(true);
    const updatedTicketStatus: TicketStatus = {
      id: ticketStatus.id,
      departmentId,
      name: ticketStatus.name,
      systemName: ticketStatus.systemName,
      isEnabled: ticketStatus.isEnabled,
    };
    const _ticketStatuses = [...ticketStatuses];
    _ticketStatuses[index] = updatedTicketStatus;
    setTicketStatuses(_ticketStatuses);
    await updateTicketStatus(updatedTicketStatus);
    await doFetchTicketStatuses();
    setIsLoading(false);
  };

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const name = event.newData.name.trim();
    if (name.length < 3) {
      toast.error("Numele statusului trebuie sa aiba cel putin 3 caractere.");
      return;
    }

    setIsLoading(true);
    const count = await countByStatus(departmentId, event.newData.id);
    if (count) {
      confirmDialog({
        header: `Confirmare actualizare status ${name}`,
        icon: "pi pi-exclamation-triangle",
        message: (
          <div className="text-xl">
            Statusul tichetului este folosit in <span className="font-bold">{count} tichete</span>. Esti sigur ca vrei sa actualizezi
            statusul?
          </div>
        ),
        acceptLabel: "Da",
        rejectLabel: "Nu",
        accept: async () => await doUpdateTicketStatus(event.index, event.newData as TicketStatus),
      });
    } else {
      await doUpdateTicketStatus(event.index, event.newData as TicketStatus);
    }
    setIsLoading(false);
  };

  return isLoading ? (
    <LoadingSpinner />
  ) : (
    <Card title="Statusuri tichete">
      <DataTable size="small" editMode="row" emptyMessage={<NoResultsFound />} value={ticketStatuses} onRowEditComplete={onRowEditComplete}>
        <Column
          className="w-5rem"
          field="isEnabled"
          body={(rowData: TicketStatus) => <InputSwitch checked={rowData.isEnabled} disabled />}
          editor={(options) => <InputSwitch checked={options.value} onChange={(e) => options.editorCallback!(e.value)} />}
        />
        <Column className="w-15rem" field="systemName" header="Nume sistem"></Column>
        <Column
          className="w-25rem"
          field="name"
          header="Nume interfata"
          body={(rowData: TicketStatus) => <InputText className="p-inputtext-sm w-full" value={rowData.name} disabled />}
          editor={(options) => (
            <InputText className="p-inputtext-sm w-full" value={options.value} onChange={(e) => options.editorCallback!(e.target.value)} />
          )}
        ></Column>
        <Column className="text-right" rowEditor />
      </DataTable>
    </Card>
  );
}
